<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useListStoreNew } from "@/stores/ListStoreNew";
import PrevNextButtons from "@/components/PrevNextButtons.vue";
import { usePagination } from "@/composables/usePagination";
import apiClient from "@/apiClient.js";
import { PlusCircleIcon, TagIcon } from "@heroicons/vue/24/solid";
import { useAuthStore } from "@/stores/AuthStore";

const list = useListStoreNew();
const route = useRoute();
const router = useRouter();
const auth = useAuthStore();

const searchTerm = ref(route.query?.search ?? "");
const categoryFilter = ref(route.query?.category_id ?? "");
const perPageOptions = [25, 50, 100];
const initialPerPage = Number(route.query?.per_page);
const perPage = ref(perPageOptions.includes(initialPerPage) ? initialPerPage : 25);
const categories = ref([]);
const showCategoryManager = ref(false);
const categoryManagerList = ref([]);
const categoryManagerLoading = ref(false);
const categoryManagerSaving = ref(false);
const categoryManagerError = ref("");
const categoryManagerSuccess = ref("");
const categoryManagerForm = reactive({
  name: "",
  description: "",
});
const canManageSuppliers = computed(() =>
  auth.hasAnyRole(["finance-department", "super"]),
);

const { setUrl, changePage } = usePagination(list, null, () => ({
  search: searchTerm.value || undefined,
  category_id: categoryFilter.value || undefined,
  per_page: perPage.value,
}));

const loadCategories = async () => {
  const { data } = await apiClient.get("/api/supplier/categories");
  categories.value = data;
};

const loadCategoryManagerData = async () => {
  categoryManagerLoading.value = true;
  categoryManagerError.value = "";
  try {
    const { data } = await apiClient.get("/api/supplier-category");
    categoryManagerList.value = Array.isArray(data) ? data : [];
  } catch (error) {
    categoryManagerError.value = error?.response?.data?.message ?? "Unable to load categories.";
    categoryManagerList.value = [];
  } finally {
    categoryManagerLoading.value = false;
  }
};

const resetCategoryManagerForm = () => {
  categoryManagerForm.name = "";
  categoryManagerForm.description = "";
};

const openCategoryManager = async () => {
  showCategoryManager.value = true;
  categoryManagerSuccess.value = "";
  await loadCategoryManagerData();
};

const closeCategoryManager = () => {
  showCategoryManager.value = false;
  categoryManagerError.value = "";
  categoryManagerSuccess.value = "";
};

const saveCategoryFromModal = async () => {
  if (!categoryManagerForm.name?.trim()) {
    categoryManagerError.value = "Category name is required.";
    return;
  }
  categoryManagerSaving.value = true;
  categoryManagerError.value = "";
  categoryManagerSuccess.value = "";
  try {
    await apiClient.post("/api/supplier-category", categoryManagerForm);
    categoryManagerSuccess.value = "Category created.";
    resetCategoryManagerForm();
    await Promise.all([loadCategoryManagerData(), loadCategories()]);
  } catch (error) {
    categoryManagerError.value =
      error?.response?.data?.message ?? "Unable to create category.";
  } finally {
    categoryManagerSaving.value = false;
  }
};

const applyFilters = () => {
  list.clearFilters();
  if (searchTerm.value) {
    list.addFilter("search", encodeURIComponent(searchTerm.value));
  }
  if (categoryFilter.value) {
    list.addFilter("category_id", categoryFilter.value);
  }
  list.addFilter("per_page", perPage.value);
};

const runSearch = () => {
  applyFilters();
  list.resetPage();
  setUrl();
};

const resetFilters = () => {
  searchTerm.value = "";
  categoryFilter.value = "";
  runSearch();
};

const viewSupplier = (supplier, event) => {
  if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) {
    return;
  }

  router.push({
    name: "supplier-view",
    params: { id: supplier.id },
  });
};

const openNewTab = (event, supplier) => {
  if (event.button === 1) {
    window.open(`/suppliers/${supplier.id}`, "_blank");
  }
};

onMounted(async () => {
  list.configure({ remote: "/api/supplier", local: "/suppliers" });
  applyFilters();
  list.fetchIndex();
  loadCategories();
});
</script>

<template>
  <div>
    <div class="box space-y-4">
      <div class="flex flex-col gap-3 lg:flex-row lg:items-center">
        <input class="input flex-1" type="text" v-model="searchTerm" placeholder="Search suppliers"
          @keyup.enter="runSearch" />
        <select v-model="categoryFilter" class="input lg:w-56" @change="runSearch">
          <option value="">All Categories</option>
          <option v-for="category in categories" :key="category.id" :value="category.id">
            {{ category.name }}
          </option>
        </select>
        <select v-model.number="perPage" class="input lg:w-36" @change="runSearch">
          <option v-for="option in perPageOptions" :key="option" :value="option">
            {{ option }} / page
          </option>
        </select>
        <button class="btn accent" @click.prevent="runSearch">
          Search
        </button>
        <button class="btn" @click.prevent="resetFilters">
          Clear
        </button>
        <router-link
          v-if="canManageSuppliers"
          to="/suppliers/create"
          class="btn accent flex items-center gap-2 whitespace-nowrap"
        >
          <PlusCircleIcon class="icon" />
          <span>New Supplier</span>
        </router-link>
        <button
          v-if="canManageSuppliers"
          class="btn flex items-center gap-2 whitespace-nowrap"
          @click.prevent="openCategoryManager"
        >
          <TagIcon class="icon" />
          <span>Manage Categories</span>
        </button>
      </div>
    </div>

    <table class="box list" v-if="!list.isLoading">
      <thead class="hidden md:table-header-group">
        <tr>
          <th class="px-4 py-2 text-left">Company</th>
          <th class="px-4 py-2 text-left w-48">Category</th>
          <th class="px-4 py-2 text-left w-40">VAT</th>
          <th class="px-4 py-2 text-left">Website</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in list.getIndex" :key="row.id"
          class="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-900 cursor-pointer"
          @click="event => viewSupplier(row, event)" @auxclick="event => openNewTab(event, row)">
          <td class="px-4 py-3">
            <div class="text-slate-900 dark:text-slate-100">{{ row.company }}</div>
            <div class="text-xs text-slate-500 md:hidden">
              {{ row.category?.name ?? "Uncategorised" }}
            </div>
            <div class="text-xs text-slate-500 truncate md:hidden">
              {{ row.address?.data }}
            </div>
          </td>
          <td class="px-4 py-3 hidden md:table-cell">
            <span class="label-clickable bg-indigo-500 w-32 text-center">
              {{ row.category?.name ?? "Uncategorised" }}
            </span>
          </td>
          <td class="px-4 py-3 hidden md:table-cell">
            {{ row.vat_number || "—" }}
          </td>
          <td class="px-4 py-3 hidden md:table-cell">
            <a v-if="row.website" :href="row.website" target="_blank" rel="noopener noreferrer"
              class="text-cyan-600 hover:underline">
              {{ row.website }}
            </a>
            <span v-else>—</span>
          </td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td colspan="4" class="px-4 py-4" v-if="list.getIndex.length < 1">
            No suppliers to display.
          </td>
        </tr>
        <tr>
          <th colspan="4" class="px-4 py-4">
            <PrevNextButtons :showPrev="list.showPrev" :showNext="list.showNext" @prev="changePage(-1)"
              @next="changePage(1)" />
          </th>
        </tr>
      </tfoot>
    </table>

    <table class="box list" v-else>
      <tbody>
        <tr v-for="n in 6" :key="n" class="border-b border-gray-300 animate-pulse">
          <td class="p-3">
            <div class="h-4 bg-slate-200 rounded w-48 mb-2"></div>
            <div class="h-3 bg-slate-200 rounded w-32"></div>
          </td>
          <td class="p-3 hidden md:table-cell">
            <div class="h-4 bg-slate-200 rounded w-32"></div>
          </td>
          <td class="p-3 hidden md:table-cell">
            <div class="h-4 bg-slate-200 rounded w-24"></div>
          </td>
          <td class="p-3 hidden md:table-cell">
            <div class="h-4 bg-slate-200 rounded w-40"></div>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="showCategoryManager" class="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/70 p-4">
      <div class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl">
        <div
          class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-slate-200 dark:border-slate-800 px-6 py-4">
          <div>
            <p class="text-sm uppercase tracking-wide text-slate-500">
              Suppliers
            </p>
            <h2 class="text-xl font-semibold text-slate-900 dark:text-white">
              Manage Categories
            </h2>
            <p class="text-sm text-slate-500 dark:text-slate-300">
              Add a new category or review the existing list without leaving this page.
            </p>
          </div>
          <button class="btn" @click.prevent="closeCategoryManager">
            Close
          </button>
        </div>

        <div class="grid gap-6 md:grid-cols-3 px-6 py-6">
          <div class="space-y-4 md:col-span-1">
            <div class="flex flex-col gap-2">
              <label class="text-sm text-slate-600 dark:text-slate-300">Name</label>
              <input v-model="categoryManagerForm.name" type="text" class="input"
                placeholder="e.g. Medical Supplies" />
            </div>
            <div class="flex flex-col gap-2">
              <label class="text-sm text-slate-600 dark:text-slate-300">Description</label>
              <textarea v-model="categoryManagerForm.description" rows="3" class="input"
                placeholder="Describe what belongs in this category"></textarea>
            </div>
            <div class="flex gap-2">
              <button class="btn" @click.prevent="resetCategoryManagerForm" :disabled="categoryManagerSaving">
                Reset
              </button>
              <button class="btn accent flex-1" @click.prevent="saveCategoryFromModal"
                :disabled="categoryManagerSaving">
                {{ categoryManagerSaving ? "Saving…" : "Add Category" }}
              </button>
            </div>
            <p v-if="categoryManagerError" class="text-sm text-red-500">
              {{ categoryManagerError }}
            </p>
            <p v-else-if="categoryManagerSuccess" class="text-sm text-emerald-500">
              {{ categoryManagerSuccess }}
            </p>
          </div>

          <div class="md:col-span-2">
            <div class="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
              <table class="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                <thead class="bg-slate-50 dark:bg-slate-800/60">
                  <tr>
                    <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Name
                    </th>
                    <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                      Description
                    </th>
                    <th class="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="categoryManagerLoading">
                    <td colspan="3" class="px-4 py-6 text-center text-slate-500">
                      Loading categories…
                    </td>
                  </tr>
                  <tr v-else-if="!categoryManagerList.length">
                    <td colspan="3" class="px-4 py-6 text-center text-slate-500">
                      No categories yet.
                    </td>
                  </tr>
                  <tr v-else v-for="category in categoryManagerList" :key="category.id"
                    class="border-b border-slate-200 dark:border-slate-800">
                    <td class="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">
                      {{ category.name }}
                    </td>
                    <td class="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                      {{ category.description || "—" }}
                    </td>
                    <td class="px-4 py-3 text-sm text-slate-500">
                      {{ category.created_at || "—" }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
